/** Klocki interfejsu. Wszystkie bezstanowe — stan trzymają strony. */

import { useEffect, useRef, type ReactNode } from "react";

import { delta as formatDelta, LABELS, ago } from "../lib/format";
import type { Freshness, MetricValue } from "../lib/types";

export function Card({
  title,
  note,
  action,
  flush,
  children,
}: {
  title?: string;
  note?: string;
  action?: ReactNode;
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="card">
      {(title || action) && (
        <header className="card__head">
          <div>
            {title && <h2 className="card__title">{title}</h2>}
            {note && <p className="card__note">{note}</p>}
          </div>
          {action}
        </header>
      )}
      <div className={flush ? "card__body card__body--flush" : "card__body"}>{children}</div>
    </section>
  );
}

export function Tile({
  label,
  value,
  muted,
  hint,
  current,
  previous,
  invertDelta,
}: {
  label: string;
  value: string;
  muted?: boolean;
  hint?: string;
  current?: number;
  previous?: number;
  /** Dla metryk, w których wzrost jest złą wiadomością (churn, CAC). */
  invertDelta?: boolean;
}) {
  const change =
    current !== undefined && previous !== undefined ? formatDelta(current, previous) : null;
  const direction = change ? (invertDelta ? ((-change.direction | 0) as -1 | 0 | 1) : change.direction) : 0;

  return (
    <article className="tile">
      <p className="tile__label" title={hint}>
        {label}
        {hint && <span aria-hidden="true">ⓘ</span>}
      </p>
      <p className={muted ? "tile__value tile__value--muted" : "tile__value"}>{value}</p>
      <p className="tile__foot">
        {change ? (
          <>
            <span
              className={
                direction > 0 ? "delta delta--up" : direction < 0 ? "delta delta--down" : "delta delta--flat"
              }
            >
              {change.text}
            </span>
            <span>wobec poprzedniego okresu</span>
          </>
        ) : (
          <span>{muted ? (hint ?? "brak danych") : " "}</span>
        )}
      </p>
    </article>
  );
}

/**
 * Liczba wiodąca widoku — dokładnie jedna na ekran.
 *
 * Powód istnienia: dwanaście kafelków o tej samej wadze wizualnej nie mają
 * hierarchii, więc oko nie ma gdzie wylądować i każde pytanie kosztuje tyle
 * samo czasu. Ta liczba jest odpowiedzią na pytanie zadawane najczęściej,
 * a `support` to trzy liczby, bez których nie da się jej zinterpretować.
 * Czwarta liczba wsparcia to już siatka kafelków, czyli z powrotem brak
 * hierarchii — dlatego jest ich trzy.
 */
export function Headline({
  label,
  value,
  current,
  previous,
  invertDelta,
  meta,
  support,
}: {
  label: string;
  value: string;
  current?: number;
  previous?: number;
  invertDelta?: boolean;
  meta?: ReactNode;
  support: { label: string; value: string; foot?: string; title?: string }[];
}) {
  const change =
    current !== undefined && previous !== undefined ? formatDelta(current, previous) : null;
  const direction = change
    ? invertDelta
      ? ((-change.direction | 0) as -1 | 0 | 1)
      : change.direction
    : 0;

  return (
    <section className="headline">
      <div>
        <p className="headline__label">{label}</p>
        <p className="headline__value">{value}</p>
        <p className="headline__foot">
          {change && (
            <span
              className={
                direction > 0
                  ? "delta delta--up"
                  : direction < 0
                    ? "delta delta--down"
                    : "delta delta--flat"
              }
            >
              {change.text} wobec poprzedniego okresu
            </span>
          )}
          {meta}
        </p>
      </div>
      <div className="headline__support">
        {support.map((item) => (
          <div className="headline__item" key={item.label} title={item.title}>
            <p>{item.label}</p>
            <p>{item.value}</p>
            <p>{item.foot ?? " "}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Sekcja widoku odpowiadająca na jedno pytanie.
 *
 * Nadkreślnik mówi, o czym jest sekcja, a `question` — na jakie pytanie
 * odpowiada. To drugie jest ważniejsze: sekcja, której nie da się opisać
 * jednym pytaniem, nie ma prawa istnieć na dashboardzie.
 */
export function Level({
  title,
  question,
  action,
  children,
}: {
  title: string;
  question?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="level">
      <header className="level__head">
        <div className="level__meta">
          <p className="level__title">{title}</p>
          {question && <p className="level__question">{question}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

/** Szuflada na to, co jest potrzebne rzadko. Zwinięta domyślnie. */
export function Drawer({
  summary,
  children,
}: {
  summary: string;
  children: ReactNode;
}) {
  return (
    <details className="drawer">
      <summary>{summary}</summary>
      <div className="drawer__body">{children}</div>
    </details>
  );
}

/**
 * Panel szczegółów wysuwany z prawej krawędzi.
 *
 * Osobny byt od `Drawer`, mimo podobnej nazwy: `Drawer` chowa rzadko potrzebną
 * treść **w** stronie, a ten panel pokazuje jeden rekord **nad** stroną, nie
 * tracąc z oczu miejsca, z którego się go otworzyło. Na tablicy zadań to jest
 * różnica między „wróciłem tam, gdzie byłem" a „szukam swojej karty od nowa".
 *
 * Escape zamyka, ognisko wraca na element, który panel otworzył — bez tego
 * osoba pracująca z klawiatury po zamknięciu ląduje na początku dokumentu.
 */
export function SidePanel({
  title,
  onClose,
  footer,
  children,
}: {
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // Najświeższe `onClose` w refie, a efekt tylko przy otwarciu i zamknięciu.
  // Wywołujący podają nową funkcję przy każdym renderze, więc efekt zależny
  // od `onClose` ruszał po każdym wpisanym znaku i przerzucał ognisko z pola
  // formularza na kontener panelu.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, []);

  return (
    <div className="sheet">
      <button
        type="button"
        className="sheet__backdrop"
        aria-label="Zamknij panel"
        onClick={onClose}
      />
      <div
        className="sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
      >
        <header className="sheet__head">
          <h2>{title}</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            Zamknij
          </button>
        </header>
        <div className="sheet__body">{children}</div>
        {footer && <footer className="sheet__foot">{footer}</footer>}
      </div>
    </div>
  );
}

export function Chip({
  tone = "quiet",
  children,
  title,
}: {
  tone?: "good" | "warn" | "crit" | "info" | "quiet";
  children: ReactNode;
  title?: string;
}) {
  return (
    <span className={`chip chip--${tone}`} title={title}>
      <i className="chip__dot" aria-hidden="true" />
      {children}
    </span>
  );
}

const STATUS_TONE: Record<string, "good" | "warn" | "crit" | "info" | "quiet"> = {
  live: "good",
  building: "info",
  maintenance: "quiet",
  sunsetting: "warn",
  archived: "quiet",
  idea: "quiet",
  active: "good",
  trialing: "info",
  past_due: "warn",
  cancelled: "warn",
  churned: "crit",
  lead: "quiet",
};

export function StatusChip({ value, dictionary }: { value: string; dictionary: Record<string, string> }) {
  return <Chip tone={STATUS_TONE[value] ?? "quiet"}>{dictionary[value] ?? value}</Chip>;
}

export function Loading({ label = "Wczytuję dane…" }: { label?: string }) {
  return (
    <div className="state" role="status">
      <div className="skeleton" style={{ width: "min(100%, 18rem)", height: "0.9rem" }} />
      <span>{label}</span>
    </div>
  );
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="state">
      <strong>{title}</strong>
      {hint && <span>{hint}</span>}
    </div>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : "Nieznany błąd";
  return (
    <div className="state" role="alert">
      <strong>Nie udało się pobrać danych</strong>
      <span>{message}</span>
    </div>
  );
}

/**
 * Świeżość źródeł. Element pilnujący samego systemu: jeżeli synchronizacja
 * padnie po cichu, wykresy pokażą spadek, a nie awarię — ten pasek jest
 * jedynym miejscem, w którym różnica jest widoczna.
 */
export function FreshnessBar({ items }: { items: Freshness[] }) {
  if (!items.length) return null;
  return (
    <div className="freshness">
      {items.map((item) => (
        <span
          key={item.source}
          className={item.stale_products ? "freshness__item freshness__item--stale" : "freshness__item"}
          title={
            item.stale_products
              ? `${item.stale_products} produkt(ów) poza SLA świeżości`
              : "wszystkie produkty w SLA"
          }
        >
          <i aria-hidden="true" />
          {LABELS.source[item.source] ?? item.source}
          <span className="dim">{ago(item.last_success_at)}</span>
        </span>
      ))}
    </div>
  );
}

/** Lista przerw w danych — bez niej wykres z dziurą wygląda jak spadek. */
export function DataGaps({ gaps }: { gaps: string[] }) {
  if (!gaps.length) return null;
  const sources = [...new Set(gaps.map((gap) => gap.split(":")[0] ?? gap))];
  return (
    <p className="gap-note">
      Brak danych z {sources.map((s) => LABELS.source[s] ?? s).join(", ")} w {gaps.length}{" "}
      {gaps.length === 1 ? "dniu" : "dniach"} tego okresu. Nie interpretuj spadków, dopóki źródło
      nie uzupełni historii.
    </p>
  );
}

/** Wartość metryki, która może nie istnieć — zawsze z powodem w podpowiedzi. */
export function MetricText({
  metric,
  render,
}: {
  metric: MetricValue | undefined;
  render: (value: number) => string;
}) {
  if (!metric || !metric.available || metric.value === null) {
    return (
      <span className="dim" title={metric?.reason ?? "brak danych"}>
        —
      </span>
    );
  }
  return <span title={metric.reason ?? undefined}>{render(metric.value)}</span>;
}

/**
 * Inicjały osoby przy karcie zadania.
 *
 * Sens jest jeden: na tablicy z pięcioma kolumnami właściciela karty odczytuje
 * się kątem oka, a nie czytając nazwisko w każdej. Znak jest `aria-hidden`,
 * bo pełne imię stoi tuż obok — czytnik ekranu ma przeczytać je raz.
 */
export function Avatar({ name }: { name: string }) {
  return (
    <span className="avatar" aria-hidden="true">
      {initials(name)}
    </span>
  );
}

function initials(name: string): string {
  const source = name.includes("@") ? (name.split("@")[0] ?? name) : name;
  const words = source.split(/[\s._-]+/).filter(Boolean);
  return (
    words
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}
